import React, { useState, useEffect, useRef } from 'react';
import { withStyles } from '@material-ui/core';
import PropTypes from 'prop-types';
import { connect, useSelector, useDispatch } from 'react-redux';
import styles from '../styles';
import Header from '~/pages/Manager/Header';
import PF2MAlertDialog from '~/components/PF2MAlertDialog';

import { useTranslation } from 'react-i18next';
import {
  getOperatorsTemplate,
  exportOperators,
  importOperators,
  getOperators,
  resetFileUploadErrors,
  addOperator,
  setOperatorsValues,
  resetOperators,
  updateOperators,
} from '~/store/manager/actions';
import PF2MDataTable from '~/components/PF2MDataTable';
import { checkDuplicated } from '~/utils';

const OperatorsTab = ({
  classes,
  operators,
  getOperators: getAll,
  getOperatorsTemplate: getTemplateAction,
  importOperators: importAction,
  addOperator: addItem,
  setOperatorsValues: updateItem,
  resetOperators: reset,
  updateOperators: update,
}) => {
  const dispatch = useDispatch();
  const { t: translate } = useTranslation();
  const [operatorsLoaded, setOperatorsLoaded] = useState(false);
  const [showDialogImport, setShowDialogImport] = useState(false);
  const [dialogMessage, setDialogMessage] = useState('');
  const [isEditing, setIsEditing] = useState(false);

  const fileUploadErrors = useSelector(state => state.manager.fileUploadErrors);
  const operatorsGroups = useSelector(state => state.manager.operatorsGroups);

  const onlyNumbersRegex = value => /^\d+$/.test(value);
  const tableRef = useRef();

  useEffect(() => {
    async function fetchOperators() {
      await getAll(true);
    }
    if (!operatorsLoaded) {
      fetchOperators();
      setOperatorsLoaded(true);
    }
  }, [operatorsLoaded, getAll]);

  useEffect(() => {
    if (fileUploadErrors && fileUploadErrors.length > 0) {
      const message = fileUploadErrors.map((error) => {
        const [rowCol, description] = error.split('-');
        const [row, col] = rowCol.split(';');
        return `${translate('validation:Row')} ${row} ${translate('validation:Col')} ${col} - ${translate(`validation:${description}`)}`;
      });
      setDialogMessage(message.join('\r\n'));
      setShowDialogImport(true);
    }
  }, [fileUploadErrors]);

  const validateLookupField = key => (rowData) => {
    if (['', '0', 0].includes(rowData[key])) {
      return { isValid: false, helperText: translate('validation: RequiredField') };
    }

    return { isValid: true };
  };


  const columns = [
    {
      editable: 'always',
      title: translate('common:Registration'),
      field: 'id',
      unique: true,
      required: true,
      validate: ({ id, tableData }) => {
        if (!id) {
          return { isValid: false, helperText: translate('validation:RequiredField') };
        }

        if (!onlyNumbersRegex(String(id.trim()))) {
          return { isValid: false, helperText: translate('validation:JustNumbers') };
        }

        const duplicated = checkDuplicated(tableData, 'id', id, tableRef);
        if (duplicated) {
          return {
            isValid: false,
            helperText: translate('validation:DuplicatedRegister'),
          };
        }

        return { isValid: true };
      },
    },
    {
      editable: 'always',
      title: translate('common:Name'),
      field: 'name',
      // unique: true,
      required: true,
      validate: ({ name, tableData }) => {
        const regex = /^[\p{L}\p{N}\s\-_|+()'º./]+$/u;

        if (!name || name.trim() === '') {
          return { isValid: false, helperText: translate('validation:RequiredField') };
        }

        if (name.length > 64) {
          return { isValid: false, helperText: translate('validation:maxLength64') };
        }

        if (!regex.test(name)) {
          return { isValid: false, helperText: translate('validation:validChars') };
        }

        const duplicated = checkDuplicated(tableData, 'name', name, tableRef);
        if (duplicated) {
          return {
            isValid: false,
            helperText: translate('validation:DuplicatedRegister'),
          };
        }

        return { isValid: true };
      },
    },
    {
      editable: 'always',
      title: translate('common:Type'),
      field: 'type',
      customType: 'filterLookup',
      lookup: [
        { name: translate('common:Normal'), value: 1 },
        { name: translate('common:Maintenance'), value: 2 },
      ],
      lookupKey: 'value',
      required: true,
      validate: validateLookupField('type'),
    },
    {
      editable: 'always',
      title: translate('common:Password'),
      field: 'password',
      required: true,
      validate: ({ password }) => {
        if (!password) {
          return { isValid: false, helperText: translate('validation:RequiredField') };
        }

        if (!onlyNumbersRegex(String(password.trim()))) {
          return { isValid: false, helperText: translate('validation:JustNumbers') };
        }

        return { isValid: true };
      },
    },
    {
      editable: 'always',
      title: translate('common:STATUS'),
      field: 'active',
      customType: 'boolean',
      lookupKey: 'value',
      lookup: [
        { name: translate('common:Inactive'), value: 0 },
        { name: translate('common:Active'), value: 1 },
      ],
      defaultFilter: isEditing ? undefined : ['1'],
      required: true,
      validate: ({ active }) => {
        if (active === null || active === undefined) {
          return { isValid: false, helperText: translate('validation:RequiredField') };
        }
        return { isValid: true };
      },
    },
    {
      editable: 'always',
      title: translate('common:Group'),
      field: 'id_group',
      customType: 'filterLookup',
      lookup: operatorsGroups
        .filter(g => g.active === 1),
      lookupKey: 'id',
      required: true,
      validate: ({ id_group }) => {
        if (!id_group || String(id_group) === '0' || String(id_group) === '') {
          return { isValid: false, helperText: translate('validation:RequiredField') };
        }
        return { isValid: true };
      },
    },
  ];

  return (
    <div className={classes.tabContainer}>
      <Header
        getTemplateAction={getTemplateAction}
        importAction={importAction}
      />
      <PF2MDataTable
        ref={tableRef}
        title={translate('common:Operator')}
        options={{
          search: true,
          selection: true,
          filtering: true,
          pageSize: 20,
          pageSizeOptions: [20, 50, 100],
          hideDelete: true,
          editableRowValidation: rowData => (rowData.id_operator < 0 && rowData.update_status !== 'I'),
          maxBodyHeight: 'calc(100vh - 350px)',
          minBodyHeight: 'calc(100vh - 350px)',
        }}
        columns={columns}
        data={operators}
        onAdd={newData => addItem(newData)}
        onChange={(newData) => {
          setIsEditing(true);
          newData.forEach((row) => {
            updateItem({
              ...row, name: row.name.trim(), id: row.id.trim(), password: row.password.trim(),
            });
          });
        }}
        onDiscard={() => {
          reset();
        }}
        onApply={() => {
          const hasPendingChanges = operators.some(row => row.update_status);
          if (!hasPendingChanges) return;
          update(operators);
        }}
      />
      <PF2MAlertDialog
        title={translate('validation:ImportErrors')}
        description={dialogMessage}
        open={showDialogImport}
        onConfirm={() => {
          setShowDialogImport(false);
          dispatch(resetFileUploadErrors());
        }}
        onClose={() => setShowDialogImport(false)}
        hasCancel={false}
      />
    </div>
  );
};

OperatorsTab.propTypes = {
  classes: PropTypes.object.isRequired,
  operators: PropTypes.array,
  getOperators: PropTypes.func,
  getOperatorsTemplate: PropTypes.func,
  importOperators: PropTypes.func,
  addOperator: PropTypes.func,
  setOperatorsValues: PropTypes.func,
  resetOperators: PropTypes.func,
  updateOperators: PropTypes.func,
};

OperatorsTab.defaultProps = {
  operators: [],
  getOperators: () => {},
  getOperatorsTemplate: () => {},
  importOperators: () => {},
  addOperator: () => {},
  setOperatorsValues: () => {},
  resetOperators: () => {},
  updateOperators: () => {},
};

const mapStateToProps = state => ({
  operators: state.manager.operators,
});

const mapDispatchToProps = {
  getOperators,
  getOperatorsTemplate,
  exportOperators,
  importOperators,
  addOperator,
  setOperatorsValues,
  resetOperators,
  updateOperators,
};

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(styles)(OperatorsTab));
